import type maplibregl from "maplibre-gl";

type SmoothFitOptions = {
  padding?: number;
  maxZoom?: number;
  durationMs?: number;
};

// Track whether a map instance has already played the entry animation.
const animatedMaps = new WeakMap<maplibregl.Map, boolean>();

/**
 * Smoothly fit bounds with a short zoom-in animation the first time it's called
 * per map instance. Subsequent calls snap immediately to avoid repeated motion.
 */
export function smoothFitBounds(
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBounds,
  opts?: SmoothFitOptions,
) {
  const padding = opts?.padding ?? 48;
  const maxZoom = opts?.maxZoom ?? 19;
  // Reduced duration for better mobile responsiveness
  const duration = opts?.durationMs ?? 2000;

  if (!map.isStyleLoaded()) {
    map.once("idle", () => smoothFitBounds(map, bounds, opts));
    return;
  }

  const camera = map.cameraForBounds(bounds, { padding, maxZoom });
  
  if (!camera || !camera.center || camera.zoom == null) {
    return;
  }

  const alreadyAnimated = animatedMaps.get(map) ?? false;
  if (!alreadyAnimated) {
    // Use flyTo for the initial "arrival" animation as it provides the
    // "zoom out then in" parabolic arc (Google Earth style).
    map.flyTo({
      center: camera.center,
      zoom: camera.zoom,
      duration,
      essential: true,
      // Lower speed = slower, more majestic flight
      speed: 0.8, 
      // Curve controls the "zoom out" apex; 1.42 is default, higher = more zoomed out
      curve: 1.5,
    });
    
    animatedMaps.set(map, true);
    return;
  }

  map.fitBounds(bounds, { padding, maxZoom, duration: 0 });
}
